import gql from "graphql-tag";
import {
	preparationFieldsFragment,
	scenarioFieldsFragment,
	treatmentFieldsFragment,
	treatmentSetFieldsFragment,
} from "../../../controllers/queries";

export const GET_AGREEMENT_DETAILS = gql`
	${treatmentSetFieldsFragment}
	${preparationFieldsFragment}
	${scenarioFieldsFragment}
	${treatmentFieldsFragment}

	query GetAgreementDetails($id: ID!) {
		baseAgreements(where: { id: $id }) {
			id
			name
			subjectMatter
			defaultPriceRate
			status
			canUserViewFinancials
			version
			country {
				name
			}
			scenariosRelevantBrands {
				Name
			}
			tags {
				type
			}
			currency {
				Name
				Code
			}
			dataSource {
				id
				name
			}
			deviceAlternativeSets {
				deviceProduct {
					UDI
					Name
				}

				alternatives {
					UDI
					Name
				}
			}
			counterpartiesConnection {
				edges {
					node {
						id
						name
					}
					role
				}
			}
			validFromDate
			validToDate
			isFirstVersion
			isLastVersion
			isExtended
			enrollingPeriods {
				from
				to
			}
			enrollingConditionSets {
				conditions {
					conditionType
					operator
					bothOperatorFirst
					bothOperatorSecond
					rangeInputStart
					rangeInputEnd
					inclusionType
					includedGenerics {
						Name
					}
					timeframeRangeType
					timeframeStartingPoint
					timeframeRangeStart
					timeframeRangeEnd
					countdownOperator
					resultSelectionType
					cycleLimitFrom
					cycleLimitTo
					rating
					therapyDecision
					attributeValue
					customCondition {
						id
						name
					}
				}
				isIndicationNotEnrollingCondition
				isDiseaseNotEnrollingCondition
				scopesConnection {
					edges {
						isExtended
						excludedOn
						node {
							scopeId
							code
							logicalOperator
							treatmentSets {
								...TreatmentSetFieldsFragment
							}
							diseases {
								Icd10Code
								Name
							}
							indications {
								id
								code
								type
								intent
								line
								mutation
								typeLocalizationKey
								intentLocalizationKey
								lineLocalizationKey
								mutationLocalizationKey
							}
							isDeleted
						}
					}
				}
			}
			eligibilityConditionTreatments {
				...TreatmentFields
			}
			eligibilityConditionSetsConnection {
				edges {
					value
					numberOfAdministeredTreatmentsOperator
					minimumPercentage
					days
					previousTestNumber
					referenceTestRangeDays
					referenceAdministrationSequenceNumber
					numberOfAdministeredRangeDays
					numberOfAdministeredTreatments
					cycleAdherence
					node {
						code
						name
						inputLabel
					}
				}
			}
			coveringFormWaitInterval
			treatmentPricesConnection {
				edges {
					node {
						...TreatmentFields
					}
					price
					basePriceType
				}
			}
			allowedFormFlexEntityTypes {
				id
				name
				localizationKey
			}
			requiredFormFlexEntityTypes {
				id
				name
				localizationKey
			}
			allowedRecordFlexEntityTypes {
				id
				name
				localizationKey
			}
			requiredRecordFlexEntityTypes {
				id
				name
				localizationKey
			}
			isCoveringIntervalEnabled
			isAllowedFormsEnabled
			isRequiredFormsEnabled
			isAllowedRecordsEnabled
			isRequiredRecordsEnabled
			scenarios(options: { sort: { order: ASC } }) {
				...ScenarioFields
			}
			dataContainers {
				type
				subType
				entityTypeName
				creationWays
				providers {
					id
				}
				timeframeRangeType
				timeframeStart
				timeframeEnd
				timeframeStartingPoint
				sharingTrigger
				sharedDataPointAttributes {
					id
					identifier
					longLabelLocalizationKey
					caption
				}
				restrictedDataPointAttributes {
					id
					identifier
					longLabelLocalizationKey
					caption
				}
			}

			currency {
				Code
				Name
			}
		}
	}
`;

export const GET_DATA_POINTS_BY_DATE = gql`
	query GetDataPointsByDate($id: ID!, $startDate: String!, $endDate: String!) {
		GetAllDataPointsByDate(id: $id, startDate: $startDate, endDate: $endDate) {
			date
			treatment {
				regulatedBy {
					BrandName
				}
			}
			quantity
			payable
			variable
			refundable
			currencyCode
		}
	}
`;

export const GET_DATA_POINTS_BY_PRESCRIPTION_DATE = gql`
	query GetDataPointsByPrescriptionDate($id: ID!, $startDate: String!, $endDate: String!) {
		GetAllDataPointsByPrescriptionDate(id: $id, startDate: $startDate, endDate: $endDate) {
			prescriptionDate
			treatment {
				regulatedBy {
					BrandName
				}
			}
			quantity
			payable
			variable
			refundable
			currencyCode
		}
	}
`;

export const RUN_SLL_AGREEMENT = gql`
	mutation RunBaseAgreement($id: ID!, $executionDate: String, $triggeredByEmail: String!, $isCaseless: Boolean) {
		ExecuteBaseAgreement(id: $id, executionDate: $executionDate, triggeredByEmail: $triggeredByEmail, isCaseless: $isCaseless)
	}
`;

export const GET_AGREEMENT_OVERVIEW = gql`
	query GetAgreementOverview($id: ID!) {
		agreements(where: { id: $id, isHidden: false }) {
			id
			canUserViewFinancials
			userOrganizationRole
			name
			status
			isApprovedByOrganization
			canParticipateInApproval
			isInsightsAvailable
			isFirstVersion
			isLastVersion
			initialVersionId
			version
			isExtended
			validFromDate
			validToDate
			isCaseless
			nextVersions {
				id
				status
				isFirstVersion
				isLastVersion
				isCaseless
				validFromDate
				validToDate
				version
				isExtended
				canParticipateInApproval
				initialVersionId
				isApprovedByOrganization
			}
		}
	}
`;

export const ADD_AGREEMENT_DECISION = gql`
	mutation AddAgreementDecision($input: AgreementDecisionInput!) {
		AddAgreementDecision(input: $input)
	}
`;

export const baseAgreementMainFields = gql`
	${treatmentSetFieldsFragment}

	fragment BaseAgreementMainFields on BaseAgreement {
		id
		version
		validFromDate
		validToDate
		lastExecutionDate
		expectedDocument
		enrollingConditionSets {
			isIndicationNotEnrollingCondition
			isDiseaseNotEnrollingCondition
			scopes {
				treatmentSets {
					...TreatmentSetFieldsFragment
				}
				indications {
					id
					code
					type
					intent
					line
					mutation
					typeLocalizationKey
					intentLocalizationKey
					lineLocalizationKey
					mutationLocalizationKey
				}
			}
		}

		scenarios {
			conditions {
				conditionType
				timeframeStart
				startingPoint
				responseType
			}
		}
	}
`;

export const GET_REPORT_DATA = gql`
	${treatmentFieldsFragment}
	${treatmentSetFieldsFragment}
	${baseAgreementMainFields}

	query GetAgreementReportData($id: ID!) {
		baseAgreements(where: { id: $id }) {
			subjectMatter
			...BaseAgreementMainFields

			financialSummaryConfigConnection {
				edges {
					isIncluded
					hasHelpMessage
					localizationKey
					position
					node {
						name
					}
				}
			}

			currency {
				Code
				Name
			}

			treatmentPricesConnection {
				edges {
					basePriceType
					price
					node {
						Code
						prices {
							Price
							ValidFromDate
							ValidToDate
							priceType
						}
					}
				}
			}

			rangeChartConfigConnection {
				edges {
					name
					order
					from
					to
					node {
						chartType
					}
				}
			}

			dotThresholdsConnection {
				edges {
					threshold
					node {
						chartType
					}
				}
			}
		}
	}
`;

export const AGREEMENT_TYPE = gql`
	query GetAgreementTypeAtt($id: ID!) {
		baseAgreements(where: { id: $id }) {
			allowedForms
			requiredForms
			enrollingConditionSets {
				conditions {
					conditionType
				}
			}
			scenarios {
				conditions {
					conditionType
				}
			}
			isCaseless
		}
	}
`;

export const UPDATE_INTERVENTION_REPORT_STATUS = gql`
	mutation UpdateInterventionReportStatus($input: InterventionReportStatusUpdateInput!) {
		UpdateInterventionReportStatus(input: $input) {
			responseStatus
			message
		}
	}
`;

export const AGREEMENTS_RUN_PLANNED_SUBSCRIPTION = gql`
	subscription OnAgreementsRunPlanned {
		agreementsRunPlanned {
			ids
			initiatorUserId
			isAgreementRunSubscriptionActive
			tenantId
		}
	}
`;

export const AGREEMENT_RUN_SUBSCRIPTION = gql`
	subscription OnAgreementRun {
		agreementRun {
			id
			name
			status
			isAgreementRunSubscriptionActive
			tenantIds
		}
	}
`;
